import memoize from 'memoize-one';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import sortBy from 'sort-by';

import { useCurrentUser, useStandardApiListRequest, useStandardApiRequest } from 'my-core/hooks';
import { hasAnyRoles } from 'my-core/user-utils';

import OnlineCourseQuestion from 'my-components/OnlineCourseQuestion';
import InfiniteScroll from 'my-elements/InfiniteScroll';

import Typography from '@mui/material/Typography';

import {
  deleteOnlineCourseQuestion,
  fetchActivityOnlineCourseQuestions,
  updateOnlineCourseQuestion,
} from 'my-actions/OnlineCourseQuestionActions';

export default function OnlineCourseQuestionsListCtnr({
  instructorId,
  onlineCourseId,
  questionableId,
  questionableType,
  questionsFilter,
}) {
  const currentUser = useCurrentUser();
  // state = { saving: false, refetchAttempted: false };

  const { performRequest: performDelete /* requestStatus: deleteStatus */ } = useStandardApiRequest({
    actionCreator: deleteOnlineCourseQuestion,
    errorMessage: true,
  });
  const deleteQuestion = useCallback(
    id => {
      performDelete(id);
    },
    [performDelete],
  );

  const { performRequest: performUpdate /* requestStatus: updateStatus */ } = useStandardApiRequest({
    actionCreator: updateOnlineCourseQuestion,
    errorMessage: true,
  });
  const updateQuestion = useCallback(
    (id, params) => {
      performUpdate(id, params);
    },
    [performUpdate],
  );

  const fetchKey = `online_course_activity_${questionableType}_${questionableId}_questions`;
  const { fetching, fetchMoreResults } = useStandardApiListRequest({
    actionCreator: fetchActivityOnlineCourseQuestions,
    params: {
      online_course_id: onlineCourseId,
      questionable_id: questionableId,
      questionable_type: questionableType,
      include: ['user', 'replies', 'replies.user', 'current_user_reactions', 'current_user_vote'],
    },
    parentRequestKey: fetchKey,
    limit: 30,
  });

  const questions = useSelector(state =>
    getQuestions(
      questionableId,
      questionableType,
      currentUser.id,
      questionsFilter,
      state.onlineCourseQuestions.items,
      state.onlineCourseQuestionReplies.items,
      state.users.items,
    ),
  );

  // Filtered here rather than by the server, so a page can hold none of this part's: keep loading until there's no more.
  if (!fetching && !questions.length && !fetchMoreResults) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
        }}
      >
        No one has asked a question yet
      </Typography>
    );
  }

  const isManager = hasAnyRoles(currentUser, 'admin') || instructorId === currentUser.id;
  return (
    <>
      {questions.map(question => (
        <OnlineCourseQuestion
          key={question.id}
          disableActivityPreview
          manager={isManager}
          onDelete={deleteQuestion}
          onUpdate={updateQuestion}
          // onRefetch={onRefetch}
          // refetchAttempted={this.state.refetchAttempted}
          // setRefetchAttempted={val => this.setState({ refetchAttempted: val })}
          owner={currentUser.id === question.user_id}
          question={question}
        />
      ))}
      <InfiniteScroll fetching={fetching} onFetchMore={fetchMoreResults} />
    </>
  );
}

const getQuestions = memoize((qId, qType, userId, filter, questions, replies, users) => {
  const repliesArr = Object.values(replies);
  return Object.values(questions)
    .filter(q => q.questionable_id === qId && q.questionable_type === qType && (!filter || filter(q)))
    .map(q => ({
      ...q,
      replies: repliesArr
        .filter(r => r.question_id === q.id)
        .map(r => ({ ...r, user: users[r.user_id] }))
        .sort(sortBy('created_at')),
      user: users[q.user_id],
      _sortingScore: (q.user_id === userId ? 2 : 0) + (q.pinned ? 1 : 0),
    }))
    .sort(sortBy('-_sortingScore', '-created_at'));
});
